import type { ICareersSearchJob } from 'src/components-v3/Careers/CareersSearch.types';

const fs = require('fs');
const path = require('path');

// Ashby API docs: https://developers.ashbyhq.com/docs/public-job-posting-api
const ASHBY_API_URL = `https://api.ashbyhq.com/posting-api/job-board/plaid?includeCompensation=true`;

type AshbyEmploymentType =
  | 'FullTime'
  | 'PartTime'
  | 'Intern'
  | 'Contract'
  | 'Temporary';

type AshbyInterval = '1 YEAR' | '1 MONTH' | '1 WEEK' | '1 DAY' | 'NONE';

/* eslint-disable @typescript-eslint/naming-convention */
const employmentTypeToCommitment: Record<AshbyEmploymentType, string> = {
  FullTime: 'Full-time',
  PartTime: 'Part-time',
  Intern: 'Internship',
  Contract: 'Contract',
  Temporary: 'Temporary',
};

const ashbyIntervalToLeverInterval: Record<AshbyInterval, string> = {
  '1 YEAR': 'per-year-salary',
  '1 MONTH': 'per-month-salary',
  '1 WEEK': 'per-week-salary',
  '1 DAY': 'per-day-wage',
  NONE: 'one-time',
};
/* eslint-enable @typescript-eslint/naming-convention */

interface AshbyCompensationComponent {
  compensationType?: string;
  interval?: string;
  currencyCode?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
}

interface AshbySecondaryLocation {
  location: string;
  address?: {
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  };
}

interface AshbyJob {
  id: string;
  title: string;
  location: string;
  secondaryLocations?: AshbySecondaryLocation[];
  department?: string;
  team?: string;
  workplaceType?: string;
  descriptionHtml?: string;
  publishedAt: string;
  employmentType?: AshbyEmploymentType;
  address?: {
    postalAddress?: {
      addressCountry?: string;
    };
  };
  jobUrl: string;
  applyUrl: string;
  compensation?: {
    scrapeableCompensationSalarySummary?: string;
    summaryComponents?: AshbyCompensationComponent[];
  };
}

interface AshbyApiResponse {
  jobs?: AshbyJob[];
}

export type Job = {
  id: string;
  text: string;
  country: string;
  workplaceType: string;
  categories: {
    team: string;
    commitment?: string;
    location: string;
    allLocations: string[];
  };
  description?: string;
  descriptionBody?: string;
  lists: {
    text: string;
    content: string;
  }[];
  additional?: string;
  hostedUrl: string;
  applyUrl: string;
  salaryRange?: {
    min?: number;
    max?: number;
    currency?: string;
    interval?: string;
  };
  salaryDescription?: string;
  createdAt: number;
  otherJobs?: ICareersSearchJob[];
};

export type Jobs = {
  [key: string]: Job;
};

function mapAshbyJobToLeverJob(ashbyJob: AshbyJob): Job {
  const primaryLocation = ashbyJob.location;
  const secondaryLocations = ashbyJob.secondaryLocations || [];
  const allLocations = [
    primaryLocation,
    ...secondaryLocations.map((s) => {
      return s.location;
    }),
  ].filter(Boolean);

  const team = ashbyJob.team || 'Engineering';
  const country =
    ashbyJob.address?.postalAddress?.addressCountry ||
    secondaryLocations[0]?.address?.addressCountry ||
    'USA';

  let salaryRange: Job['salaryRange'] | undefined;
  const salaryComponent = ashbyJob.compensation?.summaryComponents?.find(
    (c) => {
      return c.compensationType === 'Salary';
    },
  );
  if (salaryComponent?.minValue != null || salaryComponent?.maxValue != null) {
    const interval =
      ashbyIntervalToLeverInterval[salaryComponent.interval || ''];
    salaryRange = {
      min: salaryComponent.minValue ?? undefined,
      max: salaryComponent.maxValue ?? undefined,
      currency: salaryComponent.currencyCode ?? undefined,
      interval,
    };
  }

  const createdAt = new Date(ashbyJob.publishedAt).getTime();
  const commitment =
    employmentTypeToCommitment[ashbyJob.employmentType || ''] || undefined;
  const salaryDescription =
    ashbyJob.compensation?.scrapeableCompensationSalarySummary;

  return {
    id: ashbyJob.id,
    text: ashbyJob.title,
    country,
    workplaceType: ashbyJob.workplaceType || 'Remote',
    categories: {
      team,
      commitment,
      location: primaryLocation,
      allLocations,
    },
    description: ashbyJob.descriptionHtml,
    lists: [],
    hostedUrl: ashbyJob.jobUrl,
    applyUrl: ashbyJob.applyUrl,
    salaryRange,
    salaryDescription,
    createdAt,
  };
}

// Build-scoped snapshot of the job board. Job slugs are derived from a
// posting's index within its team+title group, so every entry point in one
// build (the careers homepage placeholder and the job-detail getStaticPaths)
// must compute them from the *same* snapshot or their slugs can disagree and
// produce a 404. Memoizing the fetch guarantees one snapshot: for a static
// build it lives for the whole (single-process) build; a long-lived server
// process refreshes it after a short TTL so the board doesn't go stale.
const isServerBuild = process.env.BUILD_MODE === 'server';
const JOBS_MEMO_TTL_MS = 60_000;
let memoizedJobs: Job[] | null = null;
let memoizedJobsAt = 0;

// Deterministic board for Happo pages builds: live job data diffs /careers/
// on every visual comparison. Refresh via `make happo-pages-ashby-fixture`.
const ASHBY_FIXTURE_PATH = 'e2e/happo-pages/fixtures/ashby-jobs.json';
const useAshbyFixture = process.env.HAPPO_PAGES_FIXTURES === 'true';

const Ashby = {
  api: {
    getJobs: async (retries = 3): Promise<Job[]> => {
      if (useAshbyFixture) {
        if (!memoizedJobs) {
          const data: AshbyApiResponse = JSON.parse(
            fs.readFileSync(
              path.join(process.cwd(), ASHBY_FIXTURE_PATH),
              'utf8',
            ),
          );
          memoizedJobs = (data.jobs ?? []).map(mapAshbyJobToLeverJob);
        }
        return memoizedJobs;
      }

      if (
        memoizedJobs &&
        (!isServerBuild || Date.now() - memoizedJobsAt < JOBS_MEMO_TTL_MS)
      ) {
        return memoizedJobs;
      }

      try {
        const response = await fetch(ASHBY_API_URL);
        if (!response.ok) {
          throw new Error(`Ashby API responded with status ${response.status}`);
        }

        const data: AshbyApiResponse = await response.json();

        memoizedJobs = (data.jobs ?? []).map(mapAshbyJobToLeverJob);
        memoizedJobsAt = Date.now();
        return memoizedJobs;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error fetching jobs');

        if (retries > 0) {
          // eslint-disable-next-line no-console
          console.log(`Retrying ${retries} more time(s)`);
          return Ashby.api.getJobs(retries - 1);
        }

        throw e;
      }
    },
  },
  cache: {
    get: async (id: string): Promise<Job | null | undefined> => {
      try {
        const data = await fs.readFileSync(
          path.join(process.cwd(), 'src/store/jobs.db'),
        );
        const jobs: Jobs = JSON.parse(data);
        return jobs?.[id];
      } catch {
        return undefined;
      }
    },
    set: async (jobs: Jobs): Promise<void> => {
      return await fs.writeFileSync(
        path.join(process.cwd(), 'src/store/jobs.db'),
        JSON.stringify(jobs),
      );
    },
  },
};

export default Ashby;
